/**
 * 기준안·평가지 제목에 과목(편제) 이름 넣기.
 * - 1과목: '국어 수행평가 기준안' / '국어 평가지'
 * - 여러 과목: '국어, 사회 통합 수행평가 기준안' / '국어, 사회 통합 평가지'
 * 저장된 제목이 기본(일반) 제목이거나 이 규칙으로 자동 생성된 제목이면 현재 성취기준 기준으로 다시 만든다.
 * 사용자가 직접 바꾼 제목은 그대로 둔다.
 */
export type EvaluationDocTitleKind = 'criterion' | 'sheet';

const GENERIC: Record<EvaluationDocTitleKind, string> = { criterion: '수행평가 기준안', sheet: '평가지' };
const GENERIC_ALIASES: Record<EvaluationDocTitleKind, string[]> = {
  criterion: ['수행평가 기준안', '평가 기준안', '평가기준안', '기준안'],
  sheet: ['평가지'],
};
const SUFFIX: Record<EvaluationDocTitleKind, string> = { criterion: '수행평가 기준안', sheet: '평가지' };

/** 성취기준 코드의 과목 약자 → 과목명 (편제 값이 없을 때만 사용) */
const CODE_SUBJECT: Record<string, string> = {
  국: '국어', 사: '사회', 도: '도덕', 수: '수학', 과: '과학', 실: '실과', 체: '체육', 음: '음악', 미: '미술',
  영: '영어', 바: '바른 생활', 슬: '슬기로운 생활', 즐: '즐거운 생활', 정: '정보',
};

type StandardLike = { track_text?: string | null; achievement_code?: string | null };

export function subjectNamesFromStandards(standards: StandardLike[]): string[] {
  const out: string[] = [];
  for (const st of standards) {
    let name = String(st.track_text ?? '').replace(/\s+/g, ' ').trim();
    if (!name) {
      const m = String(st.achievement_code ?? '').match(/\[?\s*\d+\s*([가-힣]+?)\s*\d/);
      if (m) name = CODE_SUBJECT[m[1]] ?? CODE_SUBJECT[m[1].charAt(0)] ?? '';
    }
    if (name && !out.includes(name)) out.push(name);
  }
  return out;
}

export function genericDocTitle(kind: EvaluationDocTitleKind): string {
  return GENERIC[kind];
}

export function subjectDocTitle(kind: EvaluationDocTitleKind, subjects: string[]): string {
  if (!subjects.length) return GENERIC[kind];
  if (subjects.length === 1) return `${subjects[0]} ${SUFFIX[kind]}`;
  return `${subjects.join(', ')} 통합 ${SUFFIX[kind]}`;
}

/** 기본 제목이거나 자동 생성(과목 + 접미어) 제목인지 */
export function isAutoDocTitle(kind: EvaluationDocTitleKind, title: string | null | undefined): boolean {
  const t = String(title ?? '').replace(/\s+/g, ' ').trim();
  if (!t) return true;
  if (GENERIC_ALIASES[kind].includes(t)) return true;
  const suffix = SUFFIX[kind];
  if (!t.endsWith(` ${suffix}`)) return false;
  const head = t.slice(0, -suffix.length - 1).replace(/ 통합$/, '');
  // 과목 목록 형태(한글 과목명들, 쉼표 구분)만 자동으로 인정
  return head.split(',').every((p) => /^[가-힣 ]{1,12}$/.test(p.trim()) && p.trim().length > 0);
}

export function resolveDocTitle(
  kind: EvaluationDocTitleKind,
  title: string | null | undefined,
  subjects: string[] | null | undefined
): string {
  const t = String(title ?? '').trim();
  if (subjects && subjects.length && isAutoDocTitle(kind, t)) return subjectDocTitle(kind, subjects);
  return t || GENERIC[kind];
}
